import { useMutation } from '@tanstack/react-query'
import { Button, Field, Icon, useToast } from '@w7/ui'
import { useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { logout } from '@/auth/oidc'
import { CmPanel } from '@/features/catalog/CmPanel'
import { PasswordInput } from '@/features/hierarchy/PasswordInput'
import '@/features/hierarchy/create.css'
import {
  changeConfirmPasswordError,
  currentPasswordError,
  isPasswordValid,
  newPasswordError,
  passwordChecks,
} from './changePasswordPolicy'
import './change-password.css'

type FormValues = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

type FormErrors = Partial<Record<keyof FormValues, string>>

const EMPTY: FormValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

function validate(values: FormValues): FormErrors {
  const errors: FormErrors = {}
  const current = currentPasswordError(values.currentPassword)
  if (current) errors.currentPassword = current
  const next = newPasswordError(values.newPassword, values.currentPassword)
  if (next) errors.newPassword = next
  const confirm = changeConfirmPasswordError(values.newPassword, values.confirmPassword)
  if (confirm) errors.confirmPassword = confirm
  return errors
}

/** Lab `/changePassword` — Strix CM Security card. */
export function ChangePasswordPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [values, setValues] = useState<FormValues>(EMPTY)
  const [touched, setTouched] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const errors = useMemo(() => validate(values), [values])
  const showErr = (key: keyof FormValues) => (touched ? errors[key] : undefined)

  const saveM = useMutation({
    mutationFn: () =>
      cmApi.securityChangePassword(authHeaders(), {
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }),
    onSuccess: () => {
      toast.success('Password changed')
      setValues(EMPTY)
      setTouched(false)
      // Lab logs out after change so the new credential is used next sign-in.
      logout()
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(validate(values)).length) return
    saveM.mutate()
  }

  const newOk = isPasswordValid(values.newPassword)
  const newLiveInvalid = Boolean(values.newPassword) && !newOk

  return (
    <CmPanel
      title="Change Password"
      subtitle="Update your CM console sign-in password"
    >
      <div className="cm-change-password">
        <form className="cm-change-password-card cm-form" onSubmit={onSubmit} noValidate>
          <div className="cm-change-password-heading">
            <Icon name="lock" size={18} />
            <h2>Change Password</h2>
          </div>

          <Field
            label="Current Password"
            required
            error={showErr('currentPassword')}
          >
            {({ id, describedBy, invalid }) => (
              <PasswordInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={values.currentPassword}
                onChange={(value) =>
                  setValues((v) => ({ ...v, currentPassword: value }))
                }
                autoComplete="current-password"
                visible={showCurrent}
                onToggleVisible={() => setShowCurrent((v) => !v)}
              />
            )}
          </Field>

          <Field label="New Password" required error={showErr('newPassword')}>
            {({ id, describedBy, invalid }) => (
              <>
                <PasswordInput
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid || newLiveInvalid}
                  valid={Boolean(values.newPassword) && newOk}
                  value={values.newPassword}
                  onChange={(value) =>
                    setValues((v) => ({ ...v, newPassword: value }))
                  }
                  autoComplete="new-password"
                  visible={showNew}
                  onToggleVisible={() => setShowNew((v) => !v)}
                />
                {values.newPassword ? (
                  <ul className="w7-create-pwchecks" aria-label="Password requirements">
                    {passwordChecks(values.newPassword).map((check) => (
                      <li key={check.key} data-passed={check.passed ? 'true' : 'false'}>
                        <Icon name={check.passed ? 'check' : 'close'} size={12} />
                        {check.label}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </Field>

          <Field
            label="Confirm Password"
            required
            error={showErr('confirmPassword')}
          >
            {({ id, describedBy, invalid }) => (
              <PasswordInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={values.confirmPassword}
                onChange={(value) =>
                  setValues((v) => ({ ...v, confirmPassword: value }))
                }
                autoComplete="new-password"
                visible={showConfirm}
                onToggleVisible={() => setShowConfirm((v) => !v)}
              />
            )}
          </Field>

          <div className="cm-change-password-actions">
            <Button type="submit" busy={saveM.isPending}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saveM.isPending}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </CmPanel>
  )
}
